import React, { useState } from 'react';
import { TVoyages } from '../../../kpiTypes';
import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { formatValueAsString } from '../../../../../utils/helpers';
import { ChartContainer } from '../../../../../shared/components/charts/ChartContainer';
import { getRegionColor } from '../../../../../utils/chartColorRegistry';

export interface IVoyagesOfOriginsChartProps {
	imoNumber: number;
	vesselVoyages: TVoyages | null;
	horizontal?: boolean;
}

const VoyagesOfOriginsChart: React.FC<IVoyagesOfOriginsChartProps> = ({ ...props }) => {
	const uniqueDestinations = Array.from(new Set(props.vesselVoyages?.dataList?.map((voyage) => voyage.destinationRegion)));
	const formatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };

	if (!props.vesselVoyages || !props.vesselVoyages.dataList || props.vesselVoyages.dataList.length === 0) {
		return null; // Return null if no voyages data is available
	}

	const transformedData = props.vesselVoyages?.dataList?.reduce(
		(acc, voyage) => {
			const { originRegion, destinationRegion } = voyage;

			// Find or initialize the group for the origin region
			let originGroup = acc.find((group) => group.originRegion === originRegion);
			if (!originGroup) {
				originGroup = { originRegion };
				acc.push(originGroup);
			}

			// Add voyage count, so +1 for each voyages has same originRegion-destinationRegion
			originGroup[destinationRegion] = (originGroup[destinationRegion] || 0) + 1;
			return acc;
		},
		[] as Record<string, any>[],
	);

	// Sort function to calculate total and sort by it
	const sortedRegions = transformedData.sort((a, b) => {
		// Calculate total for object "a"
		const totalA = Object.entries(a).reduce((sum, [key, value]) => {
			if (key !== 'originRegion' && typeof value === 'number') {
				sum += value;
			}
			return sum;
		}, 0);

		// Calculate total for object "b"
		const totalB = Object.entries(b).reduce((sum, [key, value]) => {
			if (key !== 'originRegion' && typeof value === 'number') {
				sum += value;
			}
			return sum;
		}, 0);

		// Sort descending by total (change to totalA - totalB for ascending)
		return totalB - totalA;
	});

	const [options] = useState<AgChartOptions>(() => {
		return {
			data: sortedRegions,
			series: uniqueDestinations.map((destinationRegion) => ({
				type: 'bar',
				xKey: 'originRegion',
				xName: 'Origin',
				yKey: destinationRegion,
				yName: destinationRegion,
				stacked: true,
				direction: props.horizontal ? 'horizontal' : 'vertical',
				tooltip: {
					renderer: function ({ datum, xKey, yKey }) {
						return {
							data: undefined,
							heading: `Origin: ${datum[xKey]}`,
							title: `Destination: ${destinationRegion}, Voyages:  ${formatValueAsString(datum[yKey], formatOptions, '', '', '')}`,
						};
					},
				},
				fill: getRegionColor(destinationRegion),
			})),
			axes: [
				{
					type: 'number',
					position: props.horizontal ? 'bottom' : 'left',
					title: {
						text: '# Voyages',
					},
					nice: false,
				},
				{
					type: 'category',
					position: props.horizontal ? 'left' : 'bottom',
					title: {
						text: 'Origin',
					},
				},
			],
			legend: {
				enabled: true,
				position: 'bottom',
			},
		};
	});

	return (
		<>
			{!props.vesselVoyages || props.vesselVoyages?.loading ? (
				<LoadingSpinner size="md" spinnerClassName="p-2" />
			) : (
				<ChartContainer
					data={options.data}
					filename="voyages_of_origins_data"
					buttonPosition="top-right"
				>
					<AgCharts key={`chart-${props.imoNumber}`} options={options} className="w-full h-full" />
				</ChartContainer>
			)}
		</>
	);
};

export default VoyagesOfOriginsChart;
